import { HttpClient } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { ReplaySubject } from 'rxjs';
import { AppInfo } from '../../global/environment';

type User = {
  aud: string;
  auth_time: number;
  'cognito:username': string;
  'custom:digital_id': string;
  'custom:user_state': string;
  email: string;
  event_id: string;
  exp: number;
  family_name: string;
  iat: number;
  iss: string;
  jti: string;
  name: string;
  origin_jti: string;
  sub: string;
  token_use: string;
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  userToken: ReplaySubject<string | undefined> = new ReplaySubject<string | undefined>(1);

  userInfo: User | undefined;

  user$: ReplaySubject<User> = new ReplaySubject<User>(1);

  userSignal: WritableSignal<User | undefined> = signal<User | undefined>(undefined);

  constructor(private http: HttpClient) {
    // TODO: remove this when we have updated identity management to set cookie
    // on new branch
    if (AppInfo.Environment.localDevelopment) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      window['setIDTToken'] = (value: string) => {
        const days = 1;
        const name = 'AWS_IDT';
        let expires = '';
        if (days) {
          var date = new Date();
          date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
          expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + (value || '') + expires + '; path=/';
      };
    }
  }

  loadUser(): void {
    const userToken = this.getCookie('AWS_IDT');

    if (!userToken) {
      if (!AppInfo.Environment.localDevelopment) {
        document.location.href = AppInfo.Environment.myAccountURL;
      }
      this.userToken.next(undefined);
      return;
    }

    const user = jwtDecode(userToken as string) as User;

    this.userToken.next(userToken);
    this.userInfo = user;
    this.user$.next(user);
    this.userSignal.set(user);
    this.startLogoutTimer();
  }

  private startLogoutTimer() {
    const expiryTime = this.userInfo!.exp * 1000;
    const currentTime = new Date().getTime();
    const timeout = expiryTime - currentTime;

    setTimeout(() => {
      window.location.href = `${AppInfo.Environment.myAccountURL}/logout`;
    }, timeout);
  }

  private getCookie(name: string): string | undefined {
    const value = `; ${document.cookie}`;
    let parts = value.split(`; ${name}=`);
    if (parts.length > 2) {
      parts = parts.slice(0, 2);
    }
    if (parts.length === 2) return parts.pop()?.split(';').shift();

    return undefined;
  }

  logout() {
    return this.http.post(`${AppInfo.Environment.myAccountAPI}/signout`, {}).subscribe(() => {
      window.location.href = `${AppInfo.Environment.myAccountURL}/logout`;
    });
  }
}
